import { Icon, V2Button } from '@sakhtar/design-system/react';
import { useApiGet } from '../../lib/useApiGet.js';
import { equivalenceHint, parsePriceInput, sellableUnits } from '../../lib/productCatalog.js';
import type { CatalogListResponse, CatalogProduct } from '../../lib/types/masterData.js';

/** One line of a price list while editing (ids, price as typed). */
export interface PriceLineDraft {
  productId: string;
  uomId: string;
  price: string;
}

export const MAX_PRICE_LINES = 2000;

export function draftsFromListLines(
  lines: readonly { productId: string; uomId: string; unitPriceRial: string }[],
): PriceLineDraft[] {
  return lines.map((l) => ({ productId: l.productId, uomId: l.uomId, price: l.unitPriceRial }));
}

/** Validation message, or null when the lines can be sent. Blank rows are ignored. */
export function priceLinesError(lines: readonly PriceLineDraft[]): string | null {
  const seen = new Set<string>();
  for (const [index, line] of lines.entries()) {
    if (!line.productId && !line.uomId && !line.price.trim()) continue;
    if (!line.productId || !line.uomId) return `ردیف ${index + 1}: کالا و واحد را انتخاب کنید.`;
    const parsed = parsePriceInput(line.price);
    if (parsed === null || parsed === '') {
      return `ردیف ${index + 1}: قیمت باید عدد صحیح (ریال) باشد.`;
    }
    const key = `${line.productId}:${line.uomId}`;
    if (seen.has(key)) return `ردیف ${index + 1}: این کالا با این واحد قبلاً در لیست آمده است.`;
    seen.add(key);
  }
  return null;
}

/** API payload: complete rows only. */
export function priceLinesPayload(lines: readonly PriceLineDraft[]) {
  return lines
    .filter((l) => l.productId && l.uomId && parsePriceInput(l.price))
    .map((l) => ({
      productId: l.productId,
      uomId: l.uomId,
      unitPriceRial: parsePriceInput(l.price) as string,
    }));
}

/**
 * Prices of a list, one row per product and unit. The unit dropdown offers
 * only the product's own sale units; «پر کردن از قیمت کالاها» seeds the list
 * from the per-unit prices already set on the products.
 */
export function PriceListLinesEditor({
  base,
  value,
  onChange,
}: {
  readonly base: string;
  readonly value: readonly PriceLineDraft[];
  readonly onChange: (next: PriceLineDraft[]) => void;
}) {
  const catalog = useApiGet<CatalogListResponse>(
    `${base}/api/v1/master-data/product-catalog?status=active&limit=200`,
    [base],
  );
  const products: readonly CatalogProduct[] = catalog.status === 'ready' ? catalog.data.items : [];
  const byId = new Map(products.map((p) => [p.productId, p]));
  const update = (index: number, patch: Partial<PriceLineDraft>) =>
    onChange(value.map((l, i) => (i === index ? { ...l, ...patch } : l)));

  function seedFromProducts() {
    const have = new Set(value.map((l) => `${l.productId}:${l.uomId}`));
    const added: PriceLineDraft[] = [];
    for (const product of products) {
      for (const unit of sellableUnits(product)) {
        if (unit.unitPriceRial === null || have.has(`${product.productId}:${unit.uomId}`)) continue;
        added.push({ productId: product.productId, uomId: unit.uomId, price: unit.unitPriceRial });
      }
    }
    onChange([...value, ...added].slice(0, MAX_PRICE_LINES));
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div className="sk-table-scroll">
        <table className="sk-table sk-table--compact">
          <thead>
            <tr>
              <th style={{ minWidth: 200 }}>کالا</th>
              <th style={{ minWidth: 180 }}>واحد</th>
              <th style={{ width: 160 }}>قیمت (ریال)</th>
              <th style={{ width: 40 }}></th>
            </tr>
          </thead>
          <tbody>
            {value.length === 0 ? (
              <tr>
                <td colSpan={4} style={{ color: 'var(--sk-fg-muted)' }}>
                  هنوز قلمی در این لیست نیست.
                </td>
              </tr>
            ) : null}
            {value.map((line, i) => {
              const product = byId.get(line.productId);
              const units = sellableUnits(product);
              const baseUnit = product?.units.find((u) => u.isBase);
              return (
                <tr key={i}>
                  <td>
                    <select
                      className="sk-select"
                      style={{ width: '100%' }}
                      value={line.productId}
                      onChange={(e) => {
                        const next = byId.get(e.target.value);
                        const unit = sellableUnits(next)[0];
                        update(i, {
                          productId: e.target.value,
                          uomId: unit?.uomId ?? '',
                          price: unit?.unitPriceRial ?? '',
                        });
                      }}
                    >
                      <option value="">— انتخاب کالا —</option>
                      {line.productId && !product ? (
                        <option value={line.productId}>{line.productId}</option>
                      ) : null}
                      {products.map((p) => (
                        <option key={p.productId} value={p.productId}>
                          {p.name || p.code}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td>
                    <select
                      className="sk-select"
                      style={{ width: '100%' }}
                      value={line.uomId}
                      disabled={!line.productId}
                      onChange={(e) => {
                        const unit = units.find((u) => u.uomId === e.target.value);
                        update(i, {
                          uomId: e.target.value,
                          price: line.price || unit?.unitPriceRial || '',
                        });
                      }}
                    >
                      <option value="">{line.productId ? '— واحد —' : '— ابتدا کالا —'}</option>
                      {units.map((u) => {
                        const hint = equivalenceHint(u, baseUnit);
                        return (
                          <option key={u.uomId} value={u.uomId}>
                            {hint ? `${u.uomName} (${hint})` : u.uomName}
                          </option>
                        );
                      })}
                    </select>
                  </td>
                  <td>
                    <input
                      className="sk-input ltr sk-num"
                      style={{ width: '100%' }}
                      inputMode="numeric"
                      value={line.price}
                      onChange={(e) => update(i, { price: e.target.value })}
                    />
                  </td>
                  <td>
                    <button
                      type="button"
                      className="sk-btn sk-btn--ghost sk-btn--icon sk-btn--sm"
                      style={{ color: 'var(--sk-danger)' }}
                      aria-label="حذف قلم"
                      onClick={() => onChange(value.filter((_, idx) => idx !== i))}
                    >
                      <Icon name="x" size="sm" />
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
        <V2Button
          type="button"
          variant="secondary"
          size="sm"
          icon="plus"
          disabled={value.length >= MAX_PRICE_LINES}
          onClick={() => onChange([...value, { productId: '', uomId: '', price: '' }])}
        >
          افزودن قلم
        </V2Button>
        <V2Button
          type="button"
          variant="ghost"
          size="sm"
          disabled={catalog.status !== 'ready'}
          onClick={seedFromProducts}
        >
          پر کردن از قیمت کالاها
        </V2Button>
        <span style={{ fontSize: 11, color: 'var(--sk-fg-subtle)' }}>
          در پیش‌فاکتور، با انتخاب این لیست قیمت هر قلم از همین جدول می‌آید؛ برای کالاهای بدون قیمت
          در لیست، قیمت خود کالا استفاده می‌شود.
        </span>
      </div>
    </div>
  );
}
